import {
  DavinciSsoDeferState,
  DavinciSsoFailureCode,
  DavinciSsoScenario,
  SilentSsoFailureReason,
  SsoPhase,
} from './auth-sso-attempt.model';

export const SSO_DEFER_STORAGE_KEY = 'ph-auth-sso-defer';
export const INTERACTIVE_LOGIN_STORAGE_KEY = 'ph-auth-okta-interactive';
/** Tab-local: Okta IdP cookie was already alive before this portal acquired tokens (sibling MFA). */
export const IDP_PREEXISTED_STORAGE_KEY = 'ph-auth-idp-preexisted';
export const CALLBACK_ORCHESTRATION_STORAGE_KEY = 'ph-auth-callback-orchestration';
export const CALLBACK_DEFERRED_TO_LOGIN_KEY = 'ph-auth-callback-deferred-to-login';
export const WIDGET_CALLBACK_RETRY_KEY = 'ph-auth-widget-callback-retry';
export const SSO_PHASE_STORAGE_KEY = 'ph-auth-sso-phase';
/** Set after "log out of all sessions" — skip silent SSO until interactive login widget loads. */
export const POST_LOGOUT_ALL_KEY = 'ph-auth-post-logout-all';
/** Bound Okta IdP session id for this tab (custom AS id tokens often omit `sid`). */
export const BOUND_OKTA_SESSION_KEY = 'ph-auth-okta-bound-session';

export interface BoundOktaSession {
  application: string;
  id: string;
  userId?: string;
  recordedAt: string;
}

interface SsoPhaseState {
  application: string;
  phase: SsoPhase;
  failureReason?: SilentSsoFailureReason;
  recordedAt: string;
}

function readApplicationScopedFlag(key: string, application: string): boolean {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      return false;
    }
    const parsed = JSON.parse(raw) as { application: string };
    return parsed.application === application;
  } catch {
    return false;
  }
}

function writeApplicationScopedFlag(key: string, application: string): void {
  try {
    sessionStorage.setItem(key, JSON.stringify({ application, recordedAt: new Date().toISOString() }));
  } catch {
    /* ignore */
  }
}

function clearApplicationScopedFlag(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function markCallbackOrchestrationActive(application: string): void {
  writeApplicationScopedFlag(CALLBACK_ORCHESTRATION_STORAGE_KEY, application);
}

export function readCallbackOrchestrationActive(application: string): boolean {
  return readApplicationScopedFlag(CALLBACK_ORCHESTRATION_STORAGE_KEY, application);
}

export function clearCallbackOrchestrationActive(): void {
  clearApplicationScopedFlag(CALLBACK_ORCHESTRATION_STORAGE_KEY);
}

export function markCallbackDeferredToLogin(application: string): void {
  writeApplicationScopedFlag(CALLBACK_DEFERRED_TO_LOGIN_KEY, application);
}

export function readCallbackDeferredToLogin(application: string): boolean {
  return readApplicationScopedFlag(CALLBACK_DEFERRED_TO_LOGIN_KEY, application);
}

export function clearCallbackDeferredToLogin(): void {
  clearApplicationScopedFlag(CALLBACK_DEFERRED_TO_LOGIN_KEY);
}

export function clearWidgetCallbackRetry(): void {
  clearApplicationScopedFlag(WIDGET_CALLBACK_RETRY_KEY);
}

function readSsoPhaseState(application: string): SsoPhaseState | null {
  try {
    const raw = sessionStorage.getItem(SSO_PHASE_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as SsoPhaseState;
    return parsed.application === application ? parsed : null;
  } catch {
    return null;
  }
}

export function readSsoPhase(application: string): SsoPhase {
  return readSsoPhaseState(application)?.phase ?? 'none';
}

export function isSilentSsoTerminal(application: string): boolean {
  return readSsoPhase(application) === 'silent-failed';
}

export function isSilentSsoInFlight(application: string): boolean {
  return readSsoPhase(application) === 'silent-in-flight';
}

function writeSsoPhase(
  application: string,
  phase: SsoPhase,
  failureReason?: SilentSsoFailureReason
): void {
  try {
    const state: SsoPhaseState = {
      application,
      phase,
      recordedAt: new Date().toISOString(),
      ...(failureReason ? { failureReason } : {}),
    };
    sessionStorage.setItem(SSO_PHASE_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

export function markSilentSsoInFlight(application: string): void {
  writeSsoPhase(application, 'silent-in-flight');
}

export function markSilentSsoFailed(application: string, failureReason: SilentSsoFailureReason): void {
  writeSsoPhase(application, 'silent-failed', failureReason);
}

export function markOktaTokensAcquired(application: string): void {
  writeSsoPhase(application, 'tokens-acquired');
}

export function clearSsoPhase(): void {
  clearApplicationScopedFlag(SSO_PHASE_STORAGE_KEY);
}

export function markPostLogoutAll(application: string): void {
  writeApplicationScopedFlag(POST_LOGOUT_ALL_KEY, application);
}

export function readPostLogoutAll(application: string): boolean {
  return readApplicationScopedFlag(POST_LOGOUT_ALL_KEY, application);
}

export function clearPostLogoutAll(): void {
  clearApplicationScopedFlag(POST_LOGOUT_ALL_KEY);
}

export function readBoundOktaSession(application: string): BoundOktaSession | null {
  try {
    const raw = sessionStorage.getItem(BOUND_OKTA_SESSION_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as BoundOktaSession;
    if (parsed.application !== application || !parsed.id) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeBoundOktaSession(
  application: string,
  session: { id: string; userId?: string | null }
): void {
  try {
    const payload: BoundOktaSession = {
      application,
      id: session.id,
      recordedAt: new Date().toISOString(),
    };
    if (session.userId) {
      payload.userId = session.userId;
    }
    sessionStorage.setItem(BOUND_OKTA_SESSION_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function clearBoundOktaSession(): void {
  clearApplicationScopedFlag(BOUND_OKTA_SESSION_KEY);
}

export interface LiveIdpSession {
  id: string;
  userId?: string;
}

/**
 * Logged-in tab vs live Okta session. Do not persist here — capture at login/home only.
 * ponytail: no stored bind + matching userIds cannot detect same-user session replacement.
 */
export function isBoundIdpSessionStillCurrent(
  live: LiveIdpSession | null,
  bound: BoundOktaSession | null,
  idTokenSub: string | null
): boolean {
  if (!live) {
    return false;
  }
  if (idTokenSub && live.userId && live.userId !== idTokenSub) {
    return false;
  }
  if (bound?.id && bound.id !== live.id) {
    return false;
  }
  return true;
}

/** Pull a human-readable message from an API error body (flat or `{ error: { message } }`). */
function messageFromErrorBody(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (!value || typeof value !== 'object') {
    return '';
  }

  const record = value as { message?: unknown; responseDescription?: unknown; error?: unknown };
  if (typeof record.message === 'string' && record.message) {
    return record.message;
  }
  if (typeof record.responseDescription === 'string' && record.responseDescription) {
    return record.responseDescription;
  }
  if ('error' in record) {
    return messageFromErrorBody(record.error);
  }

  return '';
}

export function extractHttpErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'error' in error) {
    const fromBody = messageFromErrorBody((error as { error: unknown }).error);
    if (fromBody) {
      return fromBody;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  // ponytail: avoid String(HttpErrorResponse) → "[object Object]"
  return '';
}

function httpStatusFromError(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: unknown }).status;
    return typeof status === 'number' ? status : undefined;
  }
  return undefined;
}

export function classifySsoFailure(error: unknown): DavinciSsoFailureCode {
  const fromMessage = classifySsoFailureMessage(extractHttpErrorMessage(error));
  if (fromMessage !== 'UNKNOWN') {
    return fromMessage;
  }
  return httpStatusFromError(error) === 500 ? 'SERVER_ERROR' : 'UNKNOWN';
}

export function classifySsoFailureMessage(message: string): DavinciSsoFailureCode {
  const normalized = message.toLowerCase();
  if (normalized.includes('no roles assigned')) {
    return 'NO_ROLES';
  }
  if (normalized.includes('portal user not found') || normalized.includes('no dealer associated')) {
    return 'PORTAL_USER_NOT_FOUND';
  }
  if (normalized.includes('not associated with this portal') || normalized.includes('wrong portal')) {
    return 'WRONG_PORTAL';
  }
  if (normalized.includes('500 - ')) {
    return 'SERVER_ERROR';
  }

  return 'UNKNOWN';
}

export const PORTAL_HANDOFF_QUERY_PARAM = 'portalHandoff';
/** Survives Okta getWithRedirect — query/referrer are gone after the IdP round-trip. */
export const PORTAL_HANDOFF_STORAGE_KEY = 'ph-auth-portal-handoff';

/** External hubs that may hand off into a Phoenix portal without sibling conflict detection. */
const EXTERNAL_HANDOFF_HOSTNAME_PATTERNS = [/\.enroll-online\.com$/];

function readReferrerOrigin(): string | null {
  if (typeof document === 'undefined' || !document.referrer) {
    return null;
  }

  try {
    return new URL(document.referrer).origin;
  } catch {
    return null;
  }
}

export function readPortalHandoffFromUrl(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return new URLSearchParams(window.location.search).get(PORTAL_HANDOFF_QUERY_PARAM) === '1';
}

export function isEnrollmentHandoffReferrer(): boolean {
  const referrerOrigin = readReferrerOrigin();
  if (!referrerOrigin) {
    return false;
  }

  try {
    const hostname = new URL(referrerOrigin).hostname;
    return EXTERNAL_HANDOFF_HOSTNAME_PATTERNS.some((pattern) => pattern.test(hostname));
  } catch {
    return false;
  }
}

/** Persist handoff signals before silent SSO leaves the page for Okta. */
export function captureExternalPortalHandoff(application: string): void {
  if (readPortalHandoffFromUrl() || isEnrollmentHandoffReferrer()) {
    writeApplicationScopedFlag(PORTAL_HANDOFF_STORAGE_KEY, application);
  }
}

export function readExternalPortalHandoff(application: string): boolean {
  return (
    readApplicationScopedFlag(PORTAL_HANDOFF_STORAGE_KEY, application) ||
    readPortalHandoffFromUrl() ||
    isEnrollmentHandoffReferrer()
  );
}

export function clearExternalPortalHandoff(): void {
  clearApplicationScopedFlag(PORTAL_HANDOFF_STORAGE_KEY);
}

export function resolveSsoScenario(code: DavinciSsoFailureCode): DavinciSsoScenario {
  if (code === 'NO_ROLES') {
    return 'NO_ROLES';
  }
  if (code === 'PORTAL_USER_NOT_FOUND' || code === 'WRONG_PORTAL') {
    return 'PORTAL_SETUP';
  }
  if (code === 'SERVER_ERROR') {
    return 'SERVER_ERROR';
  }

  return 'GENERIC';
}

export function markInteractiveOktaLogin(application: string): void {
  writeApplicationScopedFlag(INTERACTIVE_LOGIN_STORAGE_KEY, application);
}

export function readInteractiveOktaLogin(application: string): boolean {
  return readApplicationScopedFlag(INTERACTIVE_LOGIN_STORAGE_KEY, application);
}

export function clearInteractiveOktaLogin(): void {
  clearApplicationScopedFlag(INTERACTIVE_LOGIN_STORAGE_KEY);
}

export function markIdpSessionPreexisted(application: string): void {
  writeApplicationScopedFlag(IDP_PREEXISTED_STORAGE_KEY, application);
}

export function readIdpSessionPreexisted(application: string): boolean {
  return readApplicationScopedFlag(IDP_PREEXISTED_STORAGE_KEY, application);
}

export function clearIdpSessionPreexisted(): void {
  clearApplicationScopedFlag(IDP_PREEXISTED_STORAGE_KEY);
}

export function clearSsoTabState(): void {
  clearDavinciSsoDefer();
  clearInteractiveOktaLogin();
  clearIdpSessionPreexisted();
  clearCallbackOrchestrationActive();
  clearCallbackDeferredToLogin();
  clearWidgetCallbackRetry();
  clearSsoPhase();
  clearExternalPortalHandoff();
  clearBoundOktaSession();
}

/**
 * Persist a tab-local SSO failure after getOktaUser / ssologin errors.
 *
 * ponytail: preexisting IdP cookie ⇒ conflict. Ceiling: Okta dashboard (no Phoenix) + 401
 * looks like sibling MFA. Federated access_denied never reaches Davinci.
 * error == null is IdP eject (clearSsoTabState already wiped the flag).
 */
export function recordDavinciSsoDefer(application: string, error: unknown): DavinciSsoDeferState {
  const message = extractHttpErrorMessage(error);
  const code = classifySsoFailure(error);
  const resolved = resolveSsoScenario(code);
  const scenario: DavinciSsoScenario =
    error == null || readIdpSessionPreexisted(application)
      ? 'CROSS_PORTAL_CONFLICT'
      : resolved === 'GENERIC'
        ? 'NO_ROLES'
        : resolved;
  const defer: DavinciSsoDeferState = {
    application,
    code,
    message,
    scenario,
    recordedAt: new Date().toISOString(),
  };

  try {
    sessionStorage.setItem(SSO_DEFER_STORAGE_KEY, JSON.stringify(defer));
  } catch {
    // ponytail: sessionStorage can fail in private mode; defer is best-effort
  }

  return defer;
}

export function readDavinciSsoDefer(application: string): DavinciSsoDeferState | null {
  try {
    const raw = sessionStorage.getItem(SSO_DEFER_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as DavinciSsoDeferState;
    if (parsed.application !== application) {
      return null;
    }

    if (!parsed.scenario) {
      parsed.scenario = resolveSsoScenario(parsed.code);
    }

    return parsed;
  } catch {
    return null;
  }
}

export function clearDavinciSsoDefer(): void {
  try {
    sessionStorage.removeItem(SSO_DEFER_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

